const FFMI_HANDOFF_STORAGE_KEY = "calculator:ffmi-handoff:v1";
const FFMI_HANDOFF_MAX_AGE_MS = 30 * 60 * 1000;

type FfmiGender = "male" | "female";

export type FfmiHandoff = {
  gender: FfmiGender;
  height: number;
  bodyFat: number;
  weight?: number;
};

type StoredFfmiHandoff = FfmiHandoff & {
  version: 1;
  source: "body_fat";
  createdAt: number;
};

function isFiniteNumberInRange(value: unknown, min: number, max: number) {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= min &&
    value <= max
  );
}

function parseStoredHandoff(value: string, now: number): FfmiHandoff | null {
  try {
    const parsed = JSON.parse(value) as Partial<StoredFfmiHandoff>;
    if (
      parsed.version !== 1 ||
      parsed.source !== "body_fat" ||
      !isFiniteNumberInRange(parsed.createdAt, 0, now) ||
      now - (parsed.createdAt as number) > FFMI_HANDOFF_MAX_AGE_MS ||
      (parsed.gender !== "male" && parsed.gender !== "female") ||
      !isFiniteNumberInRange(parsed.height, 80, 250) ||
      !isFiniteNumberInRange(parsed.bodyFat, 3, 50) ||
      (parsed.weight !== undefined &&
        !isFiniteNumberInRange(parsed.weight, 20, 500))
    ) {
      return null;
    }

    return {
      gender: parsed.gender,
      height: parsed.height as number,
      bodyFat: parsed.bodyFat as number,
      ...(parsed.weight === undefined
        ? {}
        : { weight: parsed.weight as number }),
    };
  } catch {
    return null;
  }
}

export function storeFfmiHandoff(
  handoff: FfmiHandoff,
  storage: Storage = window.sessionStorage,
  now = Date.now(),
) {
  const payload: StoredFfmiHandoff = {
    version: 1,
    source: "body_fat",
    createdAt: now,
    ...handoff,
  };

  try {
    storage.setItem(FFMI_HANDOFF_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage can be unavailable in privacy-restricted browsers. Navigation
    // still works and the user can enter the FFMI values manually.
  }
}

export function readFfmiHandoff(
  storage: Storage = window.sessionStorage,
  now = Date.now(),
) {
  try {
    const value = storage.getItem(FFMI_HANDOFF_STORAGE_KEY);
    return value ? parseStoredHandoff(value, now) : null;
  } catch {
    return null;
  }
}

export function clearFfmiHandoff(storage: Storage = window.sessionStorage) {
  try {
    storage.removeItem(FFMI_HANDOFF_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}
